import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ModalGeneric } from '../../components/ModalGeneric';
import PaginationCustom from '../../components/PaginationCustom';
import { SelectNoCreateSingleMulti } from '../../components/Inputs/BaseSelects/SelectNoCreateSingleMulti';
import { useApi } from '../../hooks/api';
import { useGlobal } from '../../hooks/global';
import { borderColors, inactiveItens, primary, secondary } from '../../theme';
import { FeatureGuide } from '../../utils/featureGuides';
import { BiBuildingHouse } from 'react-icons/bi';

interface FeedbackRow {
  user_id: string; name: string; email: string; type: string;
  seen_at: string | null; evaluated_at: string | null; rating: number | null; comment: string | null;
}
interface FeedbackData {
  summary: { records: number; seen: number; finished: number; evaluated: number; average_rating: number | null; seen_percentage: number; evaluated_percentage: number };
  count: number; rows: FeedbackRow[];
}
interface Franchise { id: string; name: string; }

const formatPercent = (value: number) => `${Math.round(value)}%`;
const roleLabels: Record<string, string> = { responsible: 'Responsável', manager: 'Gerente', analyst: 'Analista', broker: 'Corretor', customer: 'Cliente' };

const Metric: React.FC<{ title: string; value: React.ReactNode; detail: string; color?: string }> = ({ title, value, detail, color }) => (
  <div style={{ border: `1px solid ${color || 'currentColor'}`, borderRadius: 6, padding: 16, minWidth: 160, flex: '1 1 160px' }}>
    <div style={{ fontSize: 12, opacity: .72, textTransform: 'uppercase' }}>{title}</div>
    <strong style={{ display: 'block', fontSize: 26, margin: '5px 0' }}>{value}</strong>
    <div style={{ fontSize: 12, opacity: .72 }}>{detail}</div>
  </div>
);

export const FeedbackModal: React.FC<{ guide: FeatureGuide; onClose: () => void }> = ({ guide, onClose }) => {
  const { api } = useApi();
  const { theme, notify } = useGlobal();
  const servicesRef = useRef({ api, notify });
  servicesRef.current = { api, notify };
  const [franchiseId, setFranchiseId] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<FeedbackData | null>(null);
  const [loading, setLoading] = useState(true);
  const limit = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The API client appends the selected global franchise when this parameter
      // is absent. Keep it explicit so "Geral" remains unscoped for root users.
      const query = new URLSearchParams({ page: String(page), limit: String(limit), franchise_id: franchiseId });
      const response = await servicesRef.current.api.get(`/feature-guides/${guide.code}/feedback?${query}`);
      setData(response.data);
    } catch { servicesRef.current.notify('Não foi possível carregar os feedbacks.', 'error'); }
    finally { setLoading(false); }
  }, [guide.code, franchiseId, page]);

  useEffect(() => { load(); }, [load]);
  const searchFranchises = useCallback(async (search: string) => {
    try {
      const response = await servicesRef.current.api.get('/feature-guides/feedback/franchises');
      const normalizedSearch = search.trim().toLocaleLowerCase();
      const data: Franchise[] = response.data.filter((franchise: Franchise) =>
        franchise.name.toLocaleLowerCase().includes(normalizedSearch)
      );
      return { data, formatted: data.map(franchise => ({ label: franchise.name, value: franchise.id, this: franchise })) };
    } catch {
      servicesRef.current.notify('Não foi possível carregar as imobiliárias.', 'error');
      return { data: [], formatted: [] };
    }
  }, []);
  const changeFranchise = (value: string) => { setFranchiseId(value); setPage(1); };
  const pages = Math.max(1, Math.ceil((data?.count || 0) / limit));
  const summary = data?.summary;

  return <ModalGeneric open onClose={onClose} backgroundVisible>
    <div style={{ width: '100vw', height: 'calc(100 * var(--vh))', display: 'flex', flexDirection: 'column', background: primary[theme], color: inactiveItens[theme] }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, padding: '18px 28px', borderBottom: `1px solid ${borderColors[theme]}` }}>
        <div><div style={{ fontSize: 12, color: secondary[theme], fontWeight: 700 }}>FEEDBACKS DO GUIA</div><h2 style={{ margin: '4px 0 0', fontSize: 21 }}>{guide.title}</h2></div>
        <button type="button" aria-label="Fechar feedbacks" onClick={onClose} style={{ border: 0, background: 'transparent', color: inactiveItens[theme], fontSize: 30, cursor: 'pointer' }}>×</button>
      </header>
      <main style={{ overflow: 'auto', padding: 28, flex: 1 }}>
        <div style={{ width: 350, maxWidth: '100%', marginBottom: 24 }} aria-label="Filtrar por imobiliária">
          <SelectNoCreateSingleMulti
            isRequired
            isClearable
            onChange={(value: any) => changeFranchise(value?.value ?? '')}
            noOptionsMessage="Nenhuma imobiliária encontrada"
            searchOptions={searchFranchises}
            icon={<BiBuildingHouse size={20} />}
          />
        </div>
        {summary && <>
          <section aria-label="Resumo dos feedbacks" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 28 }}>
            <Metric title="Avaliação média" value={summary.average_rating === null ? '—' : `${summary.average_rating.toFixed(1)} / 5`} detail={`${summary.evaluated} avaliações registradas`} color={secondary[theme]} />
            <Metric title="Visualização" value={formatPercent(summary.seen_percentage)} detail={`${summary.seen} de ${summary.records} registros`} />
            <Metric title="Avaliação" value={formatPercent(summary.evaluated_percentage)} detail={`${summary.evaluated} de ${summary.finished} que concluíram`} />
          </section>
          <section aria-label="Percentuais de engajamento" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 30 }}>
            {[{ label: 'Visualizaram o guia', value: summary.seen_percentage }, { label: 'Avaliaram após concluir', value: summary.evaluated_percentage }].map(item => <div key={item.label} style={{ minWidth: 260, flex: '1 1 260px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 7 }}><span>{item.label}</span><strong>{formatPercent(item.value)}</strong></div>
              <div style={{ height: 10, borderRadius: 99, background: borderColors[theme], overflow: 'hidden' }}><div style={{ width: `${Math.max(0, Math.min(100, item.value))}%`, height: '100%', background: secondary[theme] }} /></div>
            </div>)}
          </section>
        </>}
        <section aria-label="Usuários" style={{ border: `1px solid ${borderColors[theme]}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${borderColors[theme]}`, fontWeight: 600 }}>Usuários ativos</div>
          {loading && <p style={{ padding: 24, textAlign: 'center' }}>Carregando feedbacks…</p>}
          {!loading && !data?.rows.length && <p style={{ padding: 24, textAlign: 'center', opacity: .65 }}>Sem usuários ativos neste escopo.</p>}
          {!loading && data?.rows.map(row => <div key={row.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '14px 18px', borderBottom: `1px solid ${borderColors[theme]}`, flexWrap: 'wrap' }}>
            <div><strong>{row.name}</strong><div style={{ fontSize: 12, opacity: .7 }}>{roleLabels[row.type] || row.type} · {row.email}</div></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {!row.seen_at && <span style={{ padding: '5px 9px', border: `1px solid ${borderColors[theme]}`, borderRadius: 4, fontSize: 12 }}>Não visualizado</span>}
              {row.seen_at && !row.evaluated_at && <span style={{ padding: '5px 9px', border: `1px solid ${borderColors[theme]}`, borderRadius: 4, fontSize: 12 }}>Ainda não avaliado</span>}
              {row.evaluated_at && <><span style={{ color: secondary[theme], fontWeight: 700 }}>{row.rating || 0} / 5 ★</span>{row.comment && <details><summary style={{ cursor: 'pointer', fontSize: 12 }}>Ver comentário</summary><p style={{ maxWidth: 400, whiteSpace: 'pre-wrap', margin: '8px 0 0', fontSize: 13 }}>{row.comment}</p></details>}</>}
            </div>
          </div>)}
        </section>
        {!loading && pages > 1 && <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}><PaginationCustom pages={pages} page={page} disable={loading} onChange={setPage} /></div>}
      </main>
    </div>
  </ModalGeneric>;
};
